import { useTranslation } from "react-i18next";
import React from "react";
import type { Checklist } from "../Checklist";
import type { GoalTargetList } from "../GoalTargetList";
import type { MiniWorkMap } from "../MiniWorkMap";

import { PageDocsAndFilesTab, type PageDocsAndFiles } from "../DocsAndFiles/PageDocsAndFiles";
import { PageNew } from "../Page";
import { IconClipboardText, IconLogs, IconMessage, IconMessages } from "../icons";

import { DateField } from "../DateField";
import { MoveModal } from "../Modal/MoveModal";
import { PersonField } from "../PersonField";
import { PrivacyField } from "../PrivacyField";
import { BadgeStatus } from "../StatusBadge/types";
import { Tabs, useTabs } from "../Tabs";
import { Activity } from "./Activity";
import { CheckIns } from "./CheckIns";
import { DeleteModal } from "./DeleteModal";
import { Discussions } from "./Discussions";
import { Overview } from "./Overview";
import { PageHeader } from "./PageHeader";
import { RichTextHandlers } from "../RichContent/types";
import { StatusBanner } from "../ProjectPageLayout/StatusBanner";
import { GoalPermissions } from "./types";
import type { FormattedTimePreferences } from "../FormattedTime";

export namespace GoalPage {
  export interface Space {
    id: string;
    name: string;
    link: string;
  }

  export type SpaceProps =
    | {
        workmapLink: string;
        addSubgoalLink: string;
        addSubprojectLink: string;
        space: Space;
        setSpace: (space: Space) => void;
        spaceSearch: (params: { query: string }) => Promise<Space[]>;
      }
    | {
        companyWorkMapLink: string;
      };

  export interface Retrospective {
    link: string;
    content: string;
    date: Date;
    author: Person;
  }

  export interface Person {
    id: string;
    fullName: string;
    avatarUrl: string | null;
    title: string;
    profileLink: string;
  }

  export interface Contributor {
    person: Person;
    personLink: string;

    contributions: {
      role: string;
      location: string;
      link: string;
    }[];
  }

  export interface ParentGoal {
    id: string;
    name: string;
    link: string;
  }

  export interface CheckIn {
    id: string;
    author: Person | null;
    date: Date;
    content: string;
    link: string;
    commentCount: number;
    status: BadgeStatus;
    state?: "draft" | "scheduled" | "published" | null;
    scheduledAt?: string | null;
  }

  export interface Discussion {
    id: string;
    title: string;
    author: Person;
    date: Date;
    link: string;
    content: string;
    commentCount: number;
  }

  interface CommonProps {
    closeLink: string;
    reopenLink: string;
    newCheckInLink: string;
    newDiscussionLink: string;
    manageAccessLink?: string;
    exportMarkdown?: () => void;

    goalName: string;
    setGoalName: (name: string) => void;

    parentGoal: ParentGoal | null;
    setParentGoal: (goal: ParentGoal | null) => void;
    parentGoalSearch: (params: { query: string }) => Promise<ParentGoal[]>;

    champion: Person | null;
    setChampion: (person: Person | null) => void;

    reviewer: Person | null;
    setReviewer: (person: Person | null) => void;

    dueDate: DateField.ContextualDate | null;
    setDueDate: (date: DateField.ContextualDate | null) => void;
    startDate: DateField.ContextualDate | null;
    setStartDate: (date: DateField.ContextualDate | null) => void;

    contributors: Contributor[];
    targets: GoalTargetList.Target[];
    checklistItems: Checklist.ChecklistItem[];
    relatedWorkItems: MiniWorkMap.WorkItem[];
    checkIns: CheckIn[];
    discussions: Discussion[];
    checkInsLoading?: boolean;
    checkInsError?: boolean;
    onRetryCheckIns?: () => void;
    discussionsLoading?: boolean;
    discussionsError?: boolean;
    onRetryDiscussions?: () => void;
    relatedWorkLoading?: boolean;
    relatedWorkError?: boolean;
    onRetryRelatedWork?: () => void;
    childrenCount: {
      checkInsCount: number;
      discussionsCount: number;
      docsAndFilesCount: number;
    };
    docsAndFiles?: PageDocsAndFiles;
    docsAndFilesAvailable?: boolean;
    docsAndFilesLoading?: boolean;
    docsAndFilesError?: boolean;
    onRetryDocsAndFiles?: () => void;
    currentUser?: Person | null;
    status: BadgeStatus;
    state: "active" | "closed";

    closedAt: Date | null;
    retrospective: Retrospective | null;

    permissions: GoalPermissions;
    accessLevels: PrivacyField.AccessLevels;
    setAccessLevels: (levels: PrivacyField.AccessLevels) => void;

    neglectedGoal: boolean;

    championSearch: PersonField.SearchData;
    reviewerSearch: PersonField.SearchData;

    description: string;
    onDescriptionChange: (description: string | null) => Promise<boolean>;

    addTarget: GoalTargetList.AddTargetFn;
    deleteTarget: GoalTargetList.DeleteTargetFn;
    updateTarget: GoalTargetList.UpdateTargetFn;
    updateTargetValue: GoalTargetList.UpdateTargetValueFn;
    updateTargetIndex: GoalTargetList.UpdateTargetIndexFn;

    addChecklistItem?: Checklist.AddChecklistItemFn;
    deleteChecklistItem?: Checklist.DeleteChecklistItemFn;
    updateChecklistItem?: Checklist.UpdateChecklistItemFn;
    toggleChecklistItem?: Checklist.ToggleChecklistItemFn;
    updateChecklistItemIndex?: Checklist.UpdateChecklistItemIndexFn;

    deleteGoal: () => Promise<void>;

    activityFeed: React.ReactNode;

    deleteModalOpen?: boolean;
    moveModealOpen?: boolean;

    // Rich text support
    richTextHandlers: RichTextHandlers;
    localDraftKeyBase?: string;
    formattedTimePreferences: FormattedTimePreferences;
  }

  export type Props = CommonProps & SpaceProps;

  export type State = Props & {
    isDeleteModalOpen: boolean;
    openDeleteModal: () => void;
    closeDeleteModal: () => void;

    isMoveModalOpen: boolean;
    openMoveModal: () => void;
    closeMoveModal: () => void;

    onReviewGoal?: () => void;
  };
}

function useGoalPageState(props: GoalPage.Props): GoalPage.State {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(props.deleteModalOpen || false);
  const [isMoveModalOpen, setIsMoveModalOpen] = React.useState(props.moveModealOpen || false);

  return {
    ...props,

    isDeleteModalOpen,
    openDeleteModal: () => setIsDeleteModalOpen(true),
    closeDeleteModal: () => setIsDeleteModalOpen(false),

    isMoveModalOpen,
    openMoveModal: () => setIsMoveModalOpen(true),
    closeMoveModal: () => setIsMoveModalOpen(false),
  };
}

export function GoalPage(props: GoalPage.Props) {
  const { t } = useTranslation();
  const state = useGoalPageState(props);

  const tabs = useTabs("overview", [
    { id: "overview", label: t("Overview"), icon: <IconClipboardText size={14} /> },
    {
      id: "check-ins",
      label: t("Check-Ins"),
      icon: <IconMessage size={14} />,
      count: props.childrenCount.checkInsCount,
    },
    {
      id: "discussions",
      label: t("Discussions"),
      icon: <IconMessages size={14} />,
      count: props.childrenCount.discussionsCount,
    },
    {
      id: "docs-and-files",
      label: t("Docs & Files"),
      icon: <IconClipboardText size={14} />,
      count: props.childrenCount.docsAndFilesCount,
      hidden: !state.docsAndFiles && !state.docsAndFilesAvailable,
    },
    { id: "activity", label: t("Activity"), icon: <IconLogs size={14} /> },
  ]);
  const activeTab =
    !state.docsAndFiles && !state.docsAndFilesAvailable && tabs.active === "docs-and-files" ? "overview" : tabs.active;

  return (
    <>
      <PageNew title={[state.goalName]} size="fullwidth" testId="goal-page">
        <PageHeader {...state} />
        <StatusBanner
          formattedTimePreferences={state.formattedTimePreferences}
          state={state.state === "closed" ? "closed" : null}
          closedAt={state.closedAt}
          reopenLink={state.reopenLink}
          retrospectiveLink={state.retrospective?.link}
          entityName="goal"
        />
        <Tabs tabs={tabs} />

        <div className="flex-1 overflow-auto">
          {activeTab === "overview" && <Overview {...state} />}
          {activeTab === "check-ins" && <CheckIns {...state} />}
          {activeTab === "discussions" && <Discussions {...state} />}
          {activeTab === "docs-and-files" && (
            <PageDocsAndFilesTab
              docsAndFiles={state.docsAndFiles}
              formattedTimePreferences={state.formattedTimePreferences}
              loading={state.docsAndFilesLoading}
              error={state.docsAndFilesError}
              onRetry={state.onRetryDocsAndFiles}
            />
          )}
          {activeTab === "activity" && <Activity {...state} />}
        </div>

        <DeleteModal {...state} />
        {"space" in state && <MoveModal {...state} />}
      </PageNew>
    </>
  );
}
